import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { showCustomConfirmDialog } from "../../dialogs/AppConfirmDialog";
import { IReorderableItem } from "../../ReorderDialog/ReorderDialog";
import { IGoal, IGoalsDataSource } from "../models/goalModels";
import { applyCompletion, sortGoalsForDisplay } from "../utils/goalsUtility";

/** What the text dialog is open for; null while it is closed. */
export type GoalDialogMode =
  | { kind: "add"; parent: IGoal | null }
  | { kind: "edit"; goal: IGoal };

/** The sibling list the reorder dialog is open for: the goals, or one goal's sub-goals. */
export interface IGoalReorderTarget {
  parentGoalId: number | null;
  title: string;
  items: IReorderableItem[];
}

/**
 * Owns the goal tree for the reusable GoalsOverview: the list query, checking goals off
 * (optimistically, with the same rules the API applies), the staff delete and reorder, and
 * which dialog is open. The same hook serves the client's "Моите цели" and the staff page
 * on a client's profile; the staff parts are there only when the data source has `editing`.
 */
export const useGoalsOverview = (dataSource: IGoalsDataSource) => {
  const { listQueryKey, editing } = dataSource;
  const queryClient = useQueryClient();

  const { data, isLoading } = useApiQuery<IGoal[]>({
    queryKey: listQueryKey,
    endpoint: dataSource.listEndpoint,
    globalIsLoading: false,
  });

  // Every write answers with the whole tree, so the cache is replaced, not refetched.
  const { mutate } = useApiMutation<IGoal[]>();
  const { mutate: mutateOrder, isLoading: isReordering } = useApiMutation<IGoal[]>();
  const showTree = (tree: IGoal[]) => queryClient.setQueryData(listQueryKey, tree);
  const refetchTree = () => queryClient.invalidateQueries({ queryKey: listQueryKey });

  const goals = useMemo(() => sortGoalsForDisplay(data ?? []), [data]);

  // --- Checking off ------------------------------------------------------------------

  // Quick clicks can make responses overtake each other; only the latest one may replace
  // what the user already sees.
  const toggleSeq = useRef(0);

  const toggleCompleted = (goal: IGoal) => {
    const isCompleted = !goal.isCompleted;
    const current = queryClient.getQueryData<IGoal[]>(listQueryKey) ?? [];
    showTree(applyCompletion(current, goal.id, isCompleted));

    const seq = ++toggleSeq.current;
    mutate(
      {
        endpoint: dataSource.setCompletedEndpoint,
        method: "put",
        payload: { id: goal.id, isCompleted },
        disableSuccessToast: true,
      },
      {
        onSuccess: (tree) => {
          if (seq === toggleSeq.current) showTree(tree);
        },
        onError: () => {
          if (seq === toggleSeq.current) refetchTree();
        },
      },
    );
  };

  // --- Add / edit dialog -------------------------------------------------------------

  const [dialogMode, setDialogMode] = useState<GoalDialogMode | null>(null);

  const openAdd = (parent: IGoal | null = null) => setDialogMode({ kind: "add", parent });
  const openEdit = (goal: IGoal) => setDialogMode({ kind: "edit", goal });
  const closeDialog = () => setDialogMode(null);

  // --- Delete ------------------------------------------------------------------------

  const requestDelete = (goal: IGoal) => {
    if (!editing) return;

    showCustomConfirmDialog(
      <span>
        Да изтрием ли целта „{goal.text}“?
        {goal.subGoals.length > 0 ? " Подцелите също ще бъдат изтрити." : ""}
      </span>,
      () =>
        mutate(
          {
            endpoint: `${editing.deleteEndpoint}?id=${goal.id}`,
            method: "delete",
            successMessage: "Целта е изтрита.",
          },
          { onSuccess: showTree },
        ),
      undefined,
      "warning",
      "Изтрий",
      "Отказ",
    );
  };

  // --- Reorder (drag and drop in the shared ReorderDialog) ---------------------------

  // The items are taken once, when the dialog opens, so a refetch never resets a drag.
  const [reorderTarget, setReorderTarget] = useState<IGoalReorderTarget | null>(null);

  const openReorder = (parent: IGoal | null = null) =>
    setReorderTarget({
      parentGoalId: parent?.id ?? null,
      title: parent ? "Подреждане на подцели" : "Подреждане на цели",
      items: (parent ? parent.subGoals : goals).map((goal) => ({ id: goal.id, name: goal.text })),
    });

  const closeReorder = () => setReorderTarget(null);

  /** Saves the dialog's order. Completed goals still show below the open ones afterwards. */
  const saveOrder = (orderedIds: number[]) =>
    new Promise<void>((resolve, reject) => {
      if (!editing || !reorderTarget) {
        resolve();
        return;
      }

      mutateOrder(
        {
          endpoint: editing.reorderEndpoint,
          method: "put",
          payload: { userId: editing.userId, parentGoalId: reorderTarget.parentGoalId, orderedIds },
          successMessage: "Подредбата е запазена.",
        },
        {
          onSuccess: (tree) => {
            showTree(tree);
            resolve();
          },
          onError: () => reject(),
        },
      );
    });

  return {
    goals,
    isLoading,
    hasAny: goals.length > 0,
    canEdit: !!editing,
    toggleCompleted,
    dialogMode,
    openAdd,
    openEdit,
    closeDialog,
    requestDelete,
    reorderTarget,
    openReorder,
    closeReorder,
    saveOrder,
    isReordering,
  };
};
